import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Package } from "lucide-react";
import { Helmet } from "react-helmet-async";

import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchProductStoreOrder } from "../../api/fetchProductStoreOrder/fetchProductStoreOrderApi";
import type { ProductStoreOrderDetail } from "../../api/fetchProductStoreOrder/types";

export default function ProductOrderConfirmation() {
  const { uuid } = useParams<{ uuid: string }>();
  const [order, setOrder] = useState<ProductStoreOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uuid) {
      setError("Missing order id.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchProductStoreOrder(uuid)
      .then((res) => {
        if (!cancelled) setOrder(res.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load order.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center space-y-4">
        <p className="text-sm text-red-600">{error ?? "Order not found."}</p>
        <Button asChild variant="outline">
          <Link to="/product-directory">Back to product directory</Link>
        </Button>
      </div>
    );
  }

  const total = parseFloat(order.amount) || 0;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <Helmet>
        <title>Order #{order.confirmationNumber}</title>
      </Helmet>

      {/* Success hero */}
      <div className="text-center py-6">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
          <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-xl font-medium text-gray-900 dark:text-white mb-1">
          Order Confirmed
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Thank you for your purchase!
        </p>
      </div>

      {/* Confirmation details */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">
              Confirmation #
            </span>
            <p className="font-medium text-gray-900 dark:text-white">
              {order.confirmationNumber}
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Amount</span>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(total)}
            </p>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
        <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">
          Items
        </h2>
        <div className="space-y-3">
          {order.items.map((item) => {
            const unit = parseFloat(item.unitPrice) || 0;
            return (
              <div className="flex items-center gap-3" key={item.uuid}>
                {item.thumbnailUrl ? (
                  <img
                    alt={item.productName}
                    className="h-8 w-8 rounded object-cover"
                    src={item.thumbnailUrl}
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 dark:bg-slate-700">
                    <Package className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">
                    {item.productName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    × {item.quantity}
                  </p>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(unit * item.quantity)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Customer details */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
        <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">
          Customer Details
        </h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Name</span>
            <p className="text-gray-900 dark:text-white">
              {order.contactFirstName} {order.contactLastName}
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Email</span>
            <p className="text-gray-900 dark:text-white">
              {order.contactEmail}
            </p>
          </div>
          {order.contactPhone ? (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Phone</span>
              <p className="text-gray-900 dark:text-white">
                {order.contactPhone}
              </p>
            </div>
          ) : null}
        </div>
        {order.hasShippingAddress ? (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Shipping Address
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>{order.addressLine1}</p>
              {order.addressLine2 ? <p>{order.addressLine2}</p> : null}
              <p>
                {order.city}, {order.state} {order.zipCode}
              </p>
            </div>
          </div>
        ) : null}
        {order.orderNotes ? (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Order Notes
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {order.orderNotes}
            </p>
          </div>
        ) : null}
      </div>

      <div className="text-center pb-6">
        <Button asChild variant="outline">
          <Link to="/product-directory">Continue Shopping</Link>
        </Button>
      </div>
    </div>
  );
}
